import { create } from 'zustand';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

export interface Toast {
  id: string;
  type: ToastType;
  message: string;
  duration?: number; // ms, default 4000
}

interface UIState {
  locale: string;
  sidebarCollapsed: boolean;
  toasts: Toast[];
  expandedGroups: Record<string, boolean>;
  setLocale: (locale: string) => void;
  toggleSidebar: () => void;
  toggleGroup: (key: string) => void;
  addToast: (type: ToastType, message: string, duration?: number) => void;
  removeToast: (id: string) => void;
}

let toastCounter = 0;

export const useUIStore = create<UIState>((set) => ({
  locale: 'fr',
  sidebarCollapsed: false,
  toasts: [],
  expandedGroups: { Core: true, Analysis: true, Management: true, Tools: true, Admin: true },
  setLocale: (locale) => set({ locale }),
  toggleSidebar: () => set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),
  toggleGroup: (key) => set((s) => ({
    expandedGroups: { ...s.expandedGroups, [key]: !s.expandedGroups[key] },
  })),
  addToast: (type, message, duration) => {
    const id = `toast-${++toastCounter}`;
    set((s) => ({ toasts: [...s.toasts, { id, type, message, duration }] }));
  },
  removeToast: (id) =>
    set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
}));

/** Shorthand helpers callable from anywhere without hooks */
export const toast = {
  success: (msg: string) => useUIStore.getState().addToast('success', msg),
  error: (msg: string) => useUIStore.getState().addToast('error', msg, 6000),
  info: (msg: string) => useUIStore.getState().addToast('info', msg),
  warning: (msg: string) => useUIStore.getState().addToast('warning', msg, 5000),
};
